<template>
  <div>
    <PTransparentLoader :loading="isLoading">
      <template v-if="payout">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 class="main-header mb-1">
              <translate translate-context="payout.detail">Pagamento</translate>
              #{{ payout.id }}
            </h1>
            <div class="text-muted">
              {{ payout.created_at | formatDate }}
              <span v-if="payout.contract_name">- {{ payout.contract_name }}</span>
            </div>
          </div>
          <div class="d-flex gap-2">
            <CButton
              color="secondary"
              variant="outline"
              @click="$router.push({ name: 'payout' })"
            >
              <CIcon name="cipArrowLeft" class="mr-1" />
              <translate translate-context="payout.detail">Torna alla lista</translate>
            </CButton>
            <CButton
              v-if="hasPerm('contract_manager.accounting.payout.download')"
              color="success"
              @click="onDownload"
            >
              <CIcon name="cipDownload" class="mr-1" />
              <translate translate-context="payout.detail">Download Excel</translate>
            </CButton>
          </div>
        </div>

        <CRow>
          <!-- Info principale -->
          <CCol lg="8">
            <!-- Riepilogo importi -->
            <CCard class="mb-4">
              <CCardHeader>
                <strong v-translate translate-context="payout.detail">Riepilogo</strong>
              </CCardHeader>
              <CCardBody>
                <CRow>
                  <CCol sm="4" class="text-center border-right">
                    <div class="text-muted small text-uppercase mb-1">
                      <translate translate-context="payout.detail">Importo Netto</translate>
                    </div>
                    <div class="h3 font-weight-bold text-success">
                      {{ payout.paid_net | currency(payout.currency) }}
                    </div>
                  </CCol>
                  <CCol sm="4" class="text-center border-right">
                    <div class="text-muted small text-uppercase mb-1">
                      <translate translate-context="payout.detail">Importo Lordo</translate>
                    </div>
                    <div class="h3 font-weight-bold">
                      {{ payout.paid_gross | currency(payout.currency) }}
                    </div>
                  </CCol>
                  <CCol sm="4" class="text-center">
                    <div class="text-muted small text-uppercase mb-1">
                      <translate translate-context="payout.detail">Rimborsi</translate>
                    </div>
                    <div class="h3 font-weight-bold text-danger">
                      {{ payout.refunds | currency(payout.currency) }}
                    </div>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            <!-- Breakdown per PicaService -->
            <CCard v-if="payout.payout_pica_services && payout.payout_pica_services.length > 0">
              <CCardHeader>
                <strong v-translate translate-context="payout.detail">Dettaglio Eventi</strong>
              </CCardHeader>
              <CCardBody class="p-0">
                <CDataTable
                  :items="payout.payout_pica_services"
                  :fields="serviceFields"
                  hover
                >
                  <template #name="{item}">
                    <td>
                      <div class="font-weight-bold">{{ item.name }}</div>
                      <div class="text-muted small" v-if="item.event_date">
                        {{ item.event_date }}
                      </div>
                    </td>
                  </template>
                  <template #refunds="{item}">
                    <td class="text-center">
                      <span v-if="item.refunds && parseFloat(item.refunds) > 0" class="text-danger">
                        {{ item.refunds | currency(payout.currency) }}
                      </span>
                      <span v-else>---</span>
                    </td>
                  </template>
                  <template #paid_net="{item}">
                    <td class="text-right">
                      <div class="font-weight-bold text-success">
                        {{ item.paid_net | currency(payout.currency) }}
                      </div>
                    </td>
                  </template>
                  <template #download="{item}">
                    <td class="text-center">
                      <CButton
                        v-if="hasPerm('contract_manager.accounting.payout.download') && item.id"
                        color="success"
                        size="sm"
                        variant="ghost"
                        @click="onDownloadPicaService(item)"
                        v-c-tooltip="{content: $pgettext('payout.detail', 'Download Excel')}"
                      >
                        <CIcon name="cipDownload" size="lg" />
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>

            <!-- Ledger Entries -->
            <CCard v-if="payout.ledger_entries && payout.ledger_entries.length > 0" class="mt-4">
              <CCardHeader>
                <strong v-translate translate-context="payout.detail">Movimenti Contabili</strong>
              </CCardHeader>
              <CCardBody class="p-0">
                <CDataTable
                  :items="payout.ledger_entries"
                  :fields="ledgerFields"
                  hover
                  small
                >
                  <template #entry_type="{item}">
                    <td>
                      <CBadge :color="getLedgerTypeColor(item.entry_type)">
                        {{ item.entry_type }}
                      </CBadge>
                    </td>
                  </template>
                  <template #amount="{item}">
                    <td class="text-right font-weight-bold" :class="getAmountClass(item.amount)">
                      {{ item.amount | currency(item.currency || payout.currency) }}
                    </td>
                  </template>
                  <template #created_at="{item}">
                    <td class="text-muted">
                      {{ item.created_at | formatDate }}
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Sidebar info -->
          <CCol lg="4">
            <!-- Stato -->
            <CCard class="mb-4">
              <CCardHeader>
                <strong v-translate translate-context="payout.detail">Stato</strong>
              </CCardHeader>
              <CCardBody>
                <CBadge :color="getStatusColor(payout.status)" size="lg" class="px-4 py-2 mb-3">
                  {{ getStatusLabel(payout.status) }}
                </CBadge>

                <div class="mt-3" v-if="payout.requested_at">
                  <div class="text-muted small">
                    <translate translate-context="payout.detail">Richiesto il</translate>
                  </div>
                  <div>{{ payout.requested_at | formatDate }}</div>
                </div>

                <div class="mt-3" v-if="payout.paid_at">
                  <div class="text-muted small">
                    <translate translate-context="payout.detail">Pagato il</translate>
                  </div>
                  <div>{{ payout.paid_at | formatDate }}</div>
                </div>

                <!-- Upload fattura (se can_upload_invoice) -->
                <div class="mt-4" v-if="canUploadInvoice">
                  <CButton
                    color="primary"
                    block
                    @click="openInvoiceModal"
                  >
                    <CIcon name="cipUpload" class="mr-1" />
                    <translate translate-context="payout.detail">Carica fattura</translate>
                  </CButton>
                </div>
              </CCardBody>
            </CCard>

            <!-- Fattura -->
            <CCard v-if="payout.invoice || payout.iban">
              <CCardHeader>
                <strong v-translate translate-context="payout.detail">Dati Pagamento</strong>
              </CCardHeader>
              <CCardBody>
                <div v-if="payout.invoice" class="mb-3">
                  <div class="text-muted small">
                    <translate translate-context="payout.detail">Fattura</translate>
                  </div>
                  <CLink :href="payout.invoice" target="_blank" class="d-flex align-items-center">
                    <CIcon name="cipFile03" class="mr-1" />
                    <translate translate-context="payout.detail">Visualizza fattura</translate>
                  </CLink>
                </div>

                <div v-if="payout.iban" class="mb-3">
                  <div class="text-muted small">IBAN</div>
                  <div class="font-monospace">{{ payout.iban }}</div>
                </div>

                <div v-if="payout.notes">
                  <div class="text-muted small">
                    <translate translate-context="payout.detail">Note</translate>
                  </div>
                  <div>{{ payout.notes }}</div>
                </div>
              </CCardBody>
            </CCard>

            <!-- Info -->
            <CCard>
              <CCardHeader>
                <strong v-translate translate-context="payout.detail">Informazioni</strong>
              </CCardHeader>
              <CCardBody>
                <div class="mb-3">
                  <div class="text-muted small">
                    <translate translate-context="payout.detail">Creato il</translate>
                  </div>
                  <div>{{ payout.created_at | formatDate }}</div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- Invoice Upload Modal -->
        <InvoiceUploadModal
          ref="invoiceModal"
          :is-uploading="isUploadingInvoice"
          @submit="onSubmitInvoice"
        />
      </template>

      <!-- Empty state -->
      <template v-else-if="!isLoading">
        <CCard>
          <CCardBody class="text-center py-5">
            <CIcon name="cil-warning" size="3xl" class="text-warning mb-3" />
            <h4 v-translate translate-context="payout.detail">Pagamento non trovato</h4>
            <CButton color="primary" class="mt-3" @click="$router.push({ name: 'payout' })">
              <translate translate-context="payout.detail">Torna alla lista</translate>
            </CButton>
          </CCardBody>
        </CCard>
      </template>
    </PTransparentLoader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import InvoiceUploadModal from './components/InvoiceUploadModal.vue'
import {
  PAYOUT_STATUS_LABELS,
  PAYOUT_STATUS_VARIANTS
} from './constants'

export default {
  name: 'PayoutDetail',
  components: {
    PTransparentLoader,
    InvoiceUploadModal
  },
  data() {
    return {
      isLoading: true,
      serviceFields: [
        { key: 'name', label: this.$pgettext('payout.detail', 'Evento') },
        { key: 'refunds', label: this.$pgettext('payout.detail', 'Rimborsi'), _classes: 'text-center' },
        { key: 'paid_net', label: this.$pgettext('payout.detail', 'Netto'), _classes: 'text-right' },
        { key: 'download', label: '', _style: 'width: 80px', sorter: false, filter: false }
      ],
      ledgerFields: [
        { key: 'entry_type', label: this.$pgettext('payout.detail', 'Tipo') },
        { key: 'amount', label: this.$pgettext('payout.detail', 'Importo'), _classes: 'text-right' },
        { key: 'created_at', label: this.$pgettext('payout.detail', 'Data') }
      ]
    }
  },
  computed: {
    ...mapGetters('payout', [
      'payout',
      'selectedCmId',
      'isUploadingInvoice'
    ]),
    payoutId() {
      return parseInt(this.$route.params.payoutId)
    },
    canUploadInvoice() {
      if (!this.payout) return false
      return (this.payout.can_upload_invoice || this.payout.status === 'invoice_rejected') &&
             this.hasPerm('contract_manager.accounting.payout.request')
    }
  },
  methods: {
    ...mapActions('payout', [
      'fetchScopes',
      'fetchPayoutDetail',
      'uploadInvoice',
      'downloadPayout',
      'downloadPayoutPicaService'
    ]),

    getStatusLabel(status) {
      return PAYOUT_STATUS_LABELS[status] || status
    },

    getStatusColor(status) {
      return PAYOUT_STATUS_VARIANTS[status] || 'secondary'
    },

    getLedgerTypeColor(type) {
      const colors = {
        'PAYOUT': 'info',
        'ORDER_REVENUE': 'success',
        'ORDER_FEE': 'warning',
        'REFUND': 'danger'
      }
      return colors[type] || 'secondary'
    },

    getAmountClass(amount) {
      const num = parseFloat(amount)
      if (num > 0) return 'text-success'
      if (num < 0) return 'text-danger'
      return ''
    },

    openInvoiceModal() {
      if (this.$refs.invoiceModal && this.payout) {
        this.$refs.invoiceModal.open(this.payout)
      }
    },

    async onSubmitInvoice({ payoutId, invoice }) {
      try {
        await this.uploadInvoice({ payoutId, invoice })
        this.notifySuccess(this.$pgettext('payout', 'Fattura caricata con successo'))
        if (this.$refs.invoiceModal) {
          this.$refs.invoiceModal.close()
        }
        await this.fetchPayoutDetail(this.payoutId)
      } catch (error) {
        const message = error.response?.data?.detail ||
                       this.$pgettext('payout', 'Errore nel caricamento della fattura')
        this.notifyError(message)
      }
    },

    async onDownload() {
      try {
        await this.downloadPayout(this.payout.id)
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Errore nel download'))
      }
    },

    async onDownloadPicaService(picaService) {
      try {
        await this.downloadPayoutPicaService({
          payoutId: this.payout.id,
          psId: picaService.id
        })
      } catch (error) {
        this.notifyError(this.$pgettext('payout.detail', 'Errore nel download'))
      }
    },

    async loadData() {
      this.isLoading = true
      try {
        if (!this.selectedCmId) {
          await this.fetchScopes()
        }

        if (this.selectedCmId) {
          await this.fetchPayoutDetail(this.payoutId)
        }
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Errore nel caricamento del pagamento'))
      } finally {
        this.isLoading = false
      }
    }
  },
  async mounted() {
    await this.loadData()
  }
}
</script>

<style scoped>
.gap-2 > * + * {
  margin-left: 0.5rem;
}

.font-monospace {
  font-family: monospace;
}
</style>
